import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { getOrganizationSchema, getWebSiteSchema } from '../utils/seoSchemas';
import { GUIDE_ARTICLES } from '../content/guides/articles';

const GRASS_TEXTURE =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuBjYMa1iFm6OOj4aAcJFH_gkwTkRpUfH83yyHSkf75bPGwyZd8_9plR8myBsUyCYdmvcEAPMpUQ4QwzDlBdx85SFPM9IvtlcLMMkw15kByyHhajwHozjrTrf0FcEfNIyflK_L67qArYavtFu52Y7f2RYPNEZtr7boXayxnHzCiOMRTwsJxvB4GQ80ccmQ-XkUEo_hI83JUuoL-Ss4KWuzSu7pHPA_QGpcn25pfga_ATibNzNCYUwKTpphYRJGSiyTP-sKhCodrQBuPw';

const FILTER_PILLS = [
  { id: 'all', label: 'All Guides' },
  { id: 'itinerary-hub', label: 'Itineraries' },
  { id: 'stadium-guide', label: 'Stadium Guide' },
  { id: 'city-guide', label: 'City Guide' },
  { id: 'travel-tips', label: 'Travel Tips' },
  { id: 'budgeting', label: 'Budgeting' },
];

const SLUG = {
  featured: 'best-us-host-cities-world-cup-2026',
  sidebarSolid: 'budget-world-cup-2026',
  sidebarImage: 'soccer-rules-world-cup-beginners',
  rowThree: ['seattle-world-cup-2026', 'los-angeles-world-cup-2026', 'new-york-world-cup-2026'],
  rowHorizontal: ['7-day-usa-world-cup-trip', 'first-time-world-cup-guide'],
  stadiumDeep: ['lumen-field-seattle', 'sofi-stadium-los-angeles'],
};

function guideBySlug(slug) {
  return GUIDE_ARTICLES.find((g) => g.slug === slug);
}

function matchesFilter(g, filterId) {
  if (!g?.listing?.filterIds) return filterId === 'all';
  if (filterId === 'all') return true;
  return g.listing.filterIds.includes(filterId);
}

function tagClasses(tone) {
  switch (tone) {
    case 'primary':
      return 'bg-primary text-on-primary';
    case 'secondary':
      return 'bg-secondary text-on-secondary';
    case 'tertiary':
      return 'bg-tertiary text-on-tertiary';
    case 'tertiary-container':
      return 'bg-tertiary-container text-on-tertiary-container';
    default:
      return 'bg-surface-container-high text-on-surface-variant';
  }
}

function labelTextClass(tone) {
  switch (tone) {
    case 'tertiary':
      return 'text-tertiary dark:text-tertiary-fixed';
    case 'primary':
      return 'text-primary dark:text-primary-container';
    case 'tertiary-container':
      return 'text-tertiary-container dark:text-tertiary-fixed';
    default:
      return 'text-secondary dark:text-secondary-fixed';
  }
}

function accentLineClass(accent, groupHover) {
  const base = 'h-[2px] w-full transition-colors';
  const hover = groupHover ? 'group-hover:bg-primary' : '';
  switch (accent) {
    case 'secondary':
      return `${base} bg-surface-container-high dark:bg-slate-700 ${groupHover ? 'group-hover:bg-secondary' : ''}`;
    case 'tertiary':
      return `${base} bg-surface-container-high dark:bg-slate-700 ${groupHover ? 'group-hover:bg-tertiary' : ''}`;
    default:
      return `${base} bg-surface-container-high dark:bg-slate-700 ${hover}`;
  }
}

const GuidesIndex = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredAll = useMemo(
    () => GUIDE_ARTICLES.filter((g) => matchesFilter(g, activeFilter)),
    [activeFilter],
  );

  const featured = guideBySlug(SLUG.featured);
  const budget = guideBySlug(SLUG.sidebarSolid);
  const soccer = guideBySlug(SLUG.sidebarImage);
  const rowThree = SLUG.rowThree.map(guideBySlug).filter(Boolean);
  const rowHoriz = SLUG.rowHorizontal.map(guideBySlug).filter(Boolean);
  const stadiumRow = SLUG.stadiumDeep.map(guideBySlug).filter(Boolean);

  const itineraryHubSamples = useMemo(
    () => GUIDE_ARTICLES.filter((g) => g.listing?.filterIds?.includes('itinerary-hub')).slice(0, 4),
    [],
  );

  const showCurated =
    activeFilter === 'all' &&
    featured &&
    budget &&
    soccer &&
    rowThree.length === 3 &&
    rowHoriz.length === 2;

  return (
    <>
      <SEOHead
        title="World Cup 2026 USA Travel Guides | WorldCup Fan Planner"
        description="FIFA World Cup 2026 travel guides for the United States: itineraries, budgets, first-time fans, host city rankings, and soccer basics for visitors."
        schema={[getOrganizationSchema(), getWebSiteSchema()]}
      />
      <main className="overflow-x-hidden">
        <section className="relative w-full py-16 sm:py-20 px-4 sm:px-6 bg-surface-container-low dark:bg-slate-900/40 overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.07] dark:opacity-[0.12] pointer-events-none bg-cover bg-center"
            style={{ backgroundImage: `url('${GRASS_TEXTURE}')` }}
            aria-hidden
          />
          <div className="max-w-7xl mx-auto relative z-10">
            <nav className="flex flex-wrap gap-x-2 gap-y-1 mb-6 text-xs sm:text-sm font-bold uppercase tracking-widest text-secondary dark:text-secondary-fixed">
              <Link to="/about" className="hover:underline">
                Resources
              </Link>
              <span className="text-on-surface-variant dark:text-slate-500">/</span>
              <span className="text-on-surface dark:text-slate-200">Knowledge base</span>
            </nav>
            <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-headline font-black mb-6 text-on-surface dark:text-white max-w-4xl tracking-tight">
              Travel Guides
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-on-surface-variant dark:text-slate-400 max-w-2xl leading-relaxed font-body">
              Long-form guides focused on planning trips to the tournament in the United States—hotels, transit, stadium days, budgets, and sample 3/5/7-day AI
              itineraries for every host venue.
            </p>
            <div className="mt-10 sm:mt-12 flex gap-3 overflow-x-auto pb-2 nav-no-scrollbar -mx-1 px-1">
              {FILTER_PILLS.map((pill) => {
                const isActive = activeFilter === pill.id;
                return (
                  <button
                    key={pill.id}
                    type="button"
                    onClick={() => setActiveFilter(pill.id)}
                    className={`whitespace-nowrap px-5 py-2.5 rounded-full font-bold text-sm shadow-sm transition-colors shrink-0 ${
                      isActive
                        ? 'bg-primary text-on-primary'
                        : 'bg-surface-container-high dark:bg-slate-800 text-on-surface-variant dark:text-slate-300 hover:bg-surface-container-highest dark:hover:bg-slate-700'
                    }`}
                  >
                    {pill.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {!showCurated && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
            {filteredAll.length === 0 ? (
              <p className="text-on-surface-variant dark:text-slate-400">No guides match this filter.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAll.map((g) => (
                  <FilteredGuideCard key={g.slug} guide={g} />
                ))}
              </div>
            )}
          </section>
        )}

        {showCurated && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              <article className="md:col-span-8 group relative overflow-hidden rounded-xl bg-surface-container-lowest dark:bg-slate-900/80 border border-outline-variant/10 dark:border-slate-800 shadow-[rgba(0,107,37,0.04)_0px_20px_40px]">
                <Link to={`/guides/${featured.slug}`} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
                  <div className="h-64 sm:h-80 md:h-96 w-full overflow-hidden">
                    {featured.listing?.coverImage && (
                      <img
                        src={featured.listing.coverImage}
                        alt=""
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                  </div>
                  <div className="p-6 sm:p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span
                        className={`px-3 py-1 rounded text-[10px] sm:text-xs font-bold tracking-widest uppercase ${tagClasses(featured.listing?.tagTone)}`}
                      >
                        {featured.listing?.displayTag}
                      </span>
                      {featured.listing?.readMinutes != null && (
                        <span className="text-on-surface-variant dark:text-slate-400 text-sm font-medium italic">
                          {featured.listing.readMinutes} min read
                        </span>
                      )}
                    </div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-headline font-black mb-3 text-on-surface dark:text-white group-hover:text-primary dark:group-hover:text-primary-container transition-colors">
                      {featured.h1}
                    </h2>
                    <p className="text-on-surface-variant dark:text-slate-400 mb-6 text-base sm:text-lg leading-relaxed">{featured.description}</p>
                    {featured.listing?.hashtags?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {featured.listing.hashtags.map((tag) => (
                          <span key={tag} className="text-xs font-bold text-secondary dark:text-secondary-fixed">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              </article>

              <div className="md:col-span-4 flex flex-col gap-8">
                <article className="p-6 rounded-xl bg-secondary text-on-secondary flex flex-col justify-between min-h-[200px] border-t-4 border-tertiary shadow-[rgba(0,107,37,0.04)_0px_20px_40px]">
                  <Link to={`/guides/${budget.slug}`} className="flex flex-col h-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-lg">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-widest text-secondary-fixed/90">{budget.listing?.displayTag}</span>
                      <h3 className="text-xl sm:text-2xl font-headline font-black mt-2 leading-tight">{budget.h1}</h3>
                    </div>
                    <p className="mt-4 text-sm font-medium text-on-secondary/90">{budget.description}</p>
                    <span className="mt-6 flex items-center gap-2 font-bold">
                      Read guide
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-2" aria-hidden />
                    </span>
                  </Link>
                </article>

                <article className="group">
                  <Link to={`/guides/${soccer.slug}`} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
                    <div className="h-48 w-full rounded-xl overflow-hidden mb-4 border border-outline-variant/10 dark:border-slate-800">
                      {soccer.listing?.coverImage && (
                        <img
                          src={soccer.listing.coverImage}
                          alt=""
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      )}
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest text-primary dark:text-primary-container mb-2 block">
                      {soccer.listing?.displayTag}
                    </span>
                    <h3 className="text-lg sm:text-xl font-headline font-bold leading-snug text-on-surface dark:text-white group-hover:underline">
                      {soccer.h1}
                    </h3>
                  </Link>
                </article>
              </div>

              {rowThree.map((g) => (
                <article
                  key={g.slug}
                  className="md:col-span-4 group bg-surface-container-lowest dark:bg-slate-900/80 rounded-xl overflow-hidden border border-outline-variant/10 dark:border-slate-800 shadow-[rgba(0,107,37,0.04)_0px_20px_40px]"
                >
                  <Link to={`/guides/${g.slug}`} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
                    <div className="h-52 sm:h-56 relative">
                      {g.listing?.coverImage && (
                        <img src={g.listing.coverImage} alt="" className="w-full h-full object-cover" />
                      )}
                      <div
                        className={`absolute top-4 left-4 text-[10px] font-black px-2 py-1 rounded uppercase tracking-wide ${tagClasses(g.listing?.tagTone)}`}
                      >
                        {g.listing?.displayTag}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl sm:text-2xl font-headline font-black mb-2 leading-tight text-on-surface dark:text-white">{g.h1}</h3>
                      <p className="text-sm text-on-surface-variant dark:text-slate-400 mb-4 line-clamp-3">{g.description}</p>
                      <div className={accentLineClass(g.listing?.accentLine || 'primary', true)} />
                    </div>
                  </Link>
                </article>
              ))}

              {rowHoriz.map((g) => (
                <article
                  key={g.slug}
                  className="md:col-span-6 flex gap-4 sm:gap-6 bg-surface-container dark:bg-slate-800/50 rounded-xl p-4 items-center border border-outline-variant/10 dark:border-slate-700"
                >
                  <Link to={`/guides/${g.slug}`} className="flex gap-4 sm:gap-6 items-center w-full min-w-0 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg">
                    <div className="w-1/3 max-w-[140px] aspect-square rounded-lg overflow-hidden shrink-0 border border-outline-variant/10">
                      {g.listing?.coverImage && (
                        <img
                          src={g.listing.coverImage}
                          alt=""
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <span className={`text-[10px] font-black tracking-widest uppercase ${labelTextClass(g.listing?.tagTone)}`}>
                        {g.listing?.displayTag}
                      </span>
                      <h3 className="text-lg sm:text-xl font-headline font-bold leading-tight mt-1 text-on-surface dark:text-white group-hover:text-primary dark:group-hover:text-primary-container transition-colors">
                        {g.h1}
                      </h3>
                      <p className="text-xs sm:text-sm text-on-surface-variant dark:text-slate-400 mt-2 line-clamp-2">{g.description}</p>
                    </div>
                  </Link>
                </article>
              ))}

              {stadiumRow.map((g) => (
                <article
                  key={g.slug}
                  className="md:col-span-6 group bg-surface-container-lowest dark:bg-slate-900/80 rounded-xl overflow-hidden border border-outline-variant/10 dark:border-slate-800 shadow-[rgba(0,107,37,0.04)_0px_20px_40px]"
                >
                  <Link to={`/guides/${g.slug}`} className="flex flex-col sm:flex-row sm:items-stretch focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
                    <div className="sm:w-2/5 h-48 sm:h-auto min-h-[200px] relative shrink-0">
                      {g.listing?.coverImage && (
                        <img src={g.listing.coverImage} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      )}
                      <div
                        className={`absolute top-4 left-4 text-[10px] font-black px-2 py-1 rounded uppercase tracking-wide ${tagClasses(g.listing?.tagTone)}`}
                      >
                        {g.listing?.displayTag}
                      </div>
                    </div>
                    <div className="p-6 flex flex-col justify-center min-w-0">
                      <h3 className="text-xl font-headline font-black leading-tight text-on-surface dark:text-white mb-2">{g.h1}</h3>
                      <p className="text-sm text-on-surface-variant dark:text-slate-400 mb-4 line-clamp-3">{g.description}</p>
                      <span className="text-primary dark:text-primary-container font-bold text-sm inline-flex items-center gap-1">
                        Read guide <ArrowRight className="h-4 w-4" aria-hidden />
                      </span>
                    </div>
                  </Link>
                </article>
              ))}

              {itineraryHubSamples.length > 0 && (
                <div className="md:col-span-12 border-t border-outline-variant/15 dark:border-slate-700 pt-10 mt-4">
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                    <div>
                      <h2 className="text-2xl font-headline font-black text-on-surface dark:text-white">Sample AI itineraries by host city</h2>
                      <p className="text-on-surface-variant dark:text-slate-400 mt-2 max-w-3xl">
                        One hub per venue area with 3-, 5-, and 7-day tabs. Use the Itineraries filter above to browse all sixteen North American hosts.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveFilter('itinerary-hub')}
                      className="text-primary dark:text-primary-container font-bold text-sm hover:underline shrink-0 self-start sm:self-auto"
                    >
                      Show all itinerary hubs
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {itineraryHubSamples.map((g) => (
                      <Link
                        key={g.slug}
                        to={`/guides/${g.slug}`}
                        className="group p-5 rounded-xl bg-surface-container-low dark:bg-slate-800/80 border border-outline-variant/10 dark:border-slate-700 hover:border-primary/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <span className="text-[10px] font-black uppercase tracking-widest text-primary dark:text-primary-container">{g.listing?.displayTag}</span>
                        <h3 className="text-base font-headline font-bold text-on-surface dark:text-white mt-2 line-clamp-2 group-hover:underline">{g.h1}</h3>
                        <p className="text-xs text-on-surface-variant dark:text-slate-400 mt-2 line-clamp-3">{g.description}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
          <div className="bg-primary-container/30 dark:bg-primary-dim/20 rounded-3xl p-8 sm:p-12 flex flex-col lg:flex-row items-stretch lg:items-center gap-8 lg:gap-12 border-2 border-primary/10 dark:border-primary/20">
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-headline font-black mb-3 text-on-surface dark:text-white">
                Don&apos;t miss a guide update.
              </h2>
              <p className="text-base sm:text-lg text-on-primary-container/90 dark:text-slate-300">
                Get the latest stadium logistics, ticket window alerts, and city safety guides. We&apos;re not mailing yet—leave your email on our contact page for now.
              </p>
            </div>
            <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 shrink-0">
              <input
                type="email"
                placeholder="Your email address"
                disabled
                aria-label="Email for guide updates"
                className="rounded-xl border-none bg-surface-container-lowest dark:bg-slate-900 px-5 py-3.5 text-on-surface dark:text-slate-200 placeholder:text-on-surface-variant/70 focus:ring-2 focus:ring-primary w-full sm:w-72 opacity-70 cursor-not-allowed"
              />
              <Link
                to="/contact"
                className="bg-primary text-on-primary px-6 py-3.5 rounded-xl font-bold text-center hover:brightness-110 active:scale-[0.98] transition-all whitespace-nowrap"
              >
                Join Hub
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

function FilteredGuideCard({ guide }) {
  const L = guide.listing || {};
  return (
    <Link
      to={`/guides/${guide.slug}`}
      className="group block rounded-xl overflow-hidden bg-surface-container-lowest dark:bg-slate-900/80 border border-outline-variant/10 dark:border-slate-800 shadow-[rgba(0,107,37,0.04)_0px_20px_40px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="h-44 relative bg-surface-container dark:bg-slate-800">
        {L.coverImage && <img src={L.coverImage} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        {L.displayTag && (
          <span className={`absolute top-3 left-3 text-[10px] font-black px-2 py-1 rounded uppercase tracking-wide ${tagClasses(L.tagTone)}`}>
            {L.displayTag}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-headline font-bold text-on-surface dark:text-white line-clamp-2 group-hover:text-primary dark:group-hover:text-primary-container transition-colors">
          {guide.h1}
        </h3>
        <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-2 line-clamp-2">{guide.description}</p>
        {L.readMinutes != null && <p className="text-xs text-on-surface-variant mt-2 italic">{L.readMinutes} min read</p>}
      </div>
    </Link>
  );
}

export default GuidesIndex;
